<template>
  <transition name="export-modal-fade">
    <div
      v-if="visible"
      class="export-modal-overlay"
      :class="{ rtl: isRTL }"
      @click.self="$emit('cancel')"
      @wheel.prevent
      @touchmove.prevent
    >
      <div
        class="export-modal export-modal--kpis"
        role="dialog"
        aria-modal="true"
        @wheel.stop
        @touchmove.stop
      >
        <header class="export-modal__head">
          <button type="button" class="export-modal__close" @click="$emit('cancel')">&times;</button>
          <h3 class="export-modal__title">{{ $t('er_export_executive') }}</h3>
        </header>

        <div class="export-modal__body">
          <section class="export-modal__section">
            <div class="export-modal__section-head">
              <h4 class="export-modal__section-title">{{ $t('er_export_period_title') }}</h4>
              <span v-if="isLoadingPeriods" class="export-modal__hint">{{ $t('loading') }}</span>
            </div>

            <div class="export-modal__segments" role="radiogroup" :aria-label="$t('er_export_grain')">
              <button
                type="button"
                class="export-modal__seg"
                :class="{ 'is-active': !isWeekly }"
                :aria-pressed="!isWeekly"
                @click="grain = reportGrains.MONTH"
              >
                {{ $t('er_export_grain_month') }}
              </button>
              <button
                type="button"
                class="export-modal__seg"
                :class="{ 'is-active': isWeekly }"
                :aria-pressed="isWeekly"
                @click="grain = reportGrains.WEEK"
              >
                {{ $t('er_export_grain_week') }}
              </button>
            </div>

            <template v-if="isWeekly">
              <p class="export-modal__sublabel">{{ $t('er_export_week_starts') }}</p>
              <div
                class="export-modal__segments"
                role="radiogroup"
                :aria-label="$t('er_export_week_starts')"
              >
                <button
                  type="button"
                  class="export-modal__seg"
                  :class="{ 'is-active': weekStartsOn === weekStarts.SUNDAY }"
                  :aria-pressed="weekStartsOn === weekStarts.SUNDAY"
                  @click="weekStartsOn = weekStarts.SUNDAY"
                >
                  {{ $t('er_export_week_starts_sunday') }}
                </button>
                <button
                  type="button"
                  class="export-modal__seg"
                  :class="{ 'is-active': weekStartsOn === weekStarts.MONDAY }"
                  :aria-pressed="weekStartsOn === weekStarts.MONDAY"
                  @click="weekStartsOn = weekStarts.MONDAY"
                >
                  {{ $t('er_export_week_starts_monday') }}
                </button>
              </div>
            </template>

            <p v-if="periodHint" class="export-modal__hint">{{ periodHint }}</p>
            <label v-else-if="isWeekly" class="export-modal__field" :dir="textDir">
              <span>{{ $t('er_export_report_week') }}</span>
              <select
                :dir="textDir"
                v-model="selectedReportPeriod"
                :disabled="isLoadingPeriods || !reportPeriodOptions.length"
              >
                <option v-for="period in reportPeriodOptions" :key="period.key" :value="period.key">
                  {{ periodOptionLabel(period) }}
                </option>
              </select>
            </label>
            <div v-else class="export-modal__range">
              <label class="export-modal__field" :dir="textDir">
                <span>{{ $t('er_export_from_month') }}</span>
                <select
                  :dir="textDir"
                  v-model="selectedFromPeriod"
                  :disabled="isLoadingPeriods || !fromPeriodOptions.length"
                >
                  <option v-for="period in fromPeriodOptions" :key="period.key" :value="period.key">
                    {{ periodOptionLabel(period) }}
                  </option>
                </select>
              </label>
              <label class="export-modal__field" :dir="textDir">
                <span>{{ $t('er_export_to_month') }}</span>
                <select
                  :dir="textDir"
                  v-model="selectedReportPeriod"
                  :disabled="isLoadingPeriods || !toPeriodOptions.length"
                >
                  <option v-for="period in toPeriodOptions" :key="period.key" :value="period.key">
                    {{ periodOptionLabel(period) }}
                  </option>
                </select>
              </label>
            </div>
          </section>

          <section class="export-modal__section">
            <div class="export-modal__kpis-head">
              <h4 class="export-modal__section-title">
                {{ $t('er_export_shift') }}
                <span class="export-modal__count">{{ selectedShiftIds.length }}/{{ availableShifts.length }}</span>
              </h4>
              <button
                type="button"
                class="export-modal__link"
                :disabled="isLoadingShifts || !availableShifts.length"
                @click="toggleAllShifts"
              >
                {{ $t(allShiftsSelected ? 'er_export_clear_all' : 'er_export_select_all') }}
              </button>
            </div>
            <p v-if="isLoadingShifts" class="export-modal__hint">{{ $t('loading') }}</p>
            <p v-else-if="!availableShifts.length" class="export-modal__hint">{{ $t('er_export_no_shifts') }}</p>
            <div
              v-else
              class="export-modal__checks"
              role="group"
              :aria-label="$t('er_export_shift')"
              :dir="textDir"
            >
              <label
                v-for="shift in availableShifts"
                :key="shift.id"
                class="export-modal__chip"
                :class="{ 'is-on': selectedShiftIds.includes(shift.id) }"
              >
                <input
                  type="checkbox"
                  :checked="selectedShiftIds.includes(shift.id)"
                  @change="toggleShift(shift.id)"
                />
                <span>{{ shift.name }}</span>
              </label>
            </div>
          </section>

          <section class="export-modal__section">
            <div class="export-modal__compare-toggle">
              <h4 class="export-modal__section-title">{{ $t('er_export_include_previous') }}</h4>
              <div
                class="export-modal__segments export-modal__segments--inline"
                role="radiogroup"
                :aria-label="$t('er_export_include_previous')"
              >
                <button
                  type="button"
                  class="export-modal__seg"
                  :class="{ 'is-active': comparisonOn }"
                  :aria-pressed="comparisonOn"
                  :disabled="!canCompare"
                  @click="comparisonOn = true"
                >
                  {{ $t('er_export_comparison_on') }}
                </button>
                <button
                  type="button"
                  class="export-modal__seg"
                  :class="{ 'is-active': !comparisonOn }"
                  :aria-pressed="!comparisonOn"
                  @click="comparisonOn = false"
                >
                  {{ $t('er_export_comparison_off') }}
                </button>
              </div>
            </div>

            <p v-if="showNoPreviousDataHint" class="export-modal__hint">
              {{ $t('er_export_no_previous_data') }}
            </p>
            <p
              v-else-if="comparisonOn && !periodHint && isMonthRange"
              class="export-modal__compare-value"
              :dir="textDir"
            >{{ rangeCompareLabel }}</p>
            <template v-else-if="comparisonOn && !periodHint">
              <label class="export-modal__field" :dir="textDir">
                <span>{{ $t('er_export_compare_month') }}</span>
                <select
                  :dir="textDir"
                  v-model="selectedComparePeriod"
                  :disabled="isLoadingPeriods || !comparePeriodOptions.length"
                >
                  <option v-for="period in comparePeriodOptions" :key="period.key" :value="period.key">
                    {{ periodOptionLabel(period) }}
                  </option>
                </select>
              </label>
              <div class="export-modal__presets">
                <button
                  v-for="preset in comparePresets"
                  :key="preset.id"
                  type="button"
                  class="export-modal__preset"
                  :class="{ 'is-active': !!preset.key && selectedComparePeriod === preset.key }"
                  :disabled="!preset.key"
                  @click="selectedComparePeriod = preset.key"
                >
                  {{ $t(preset.labelKey) }}
                </button>
              </div>
            </template>
          </section>

          <section class="export-modal__section">
            <div class="export-modal__kpis-head">
              <h4 class="export-modal__section-title">
                {{ $t('er_export_kpis_title') }}
                <span class="export-modal__count">{{ selectedKpiIds.length }}/{{ kpis.length }}</span>
              </h4>
              <button type="button" class="export-modal__link" @click="toggleAllKpis">
                {{ $t(allKpisSelected ? 'er_export_clear_all' : 'er_export_select_all') }}
              </button>
            </div>
            <p class="export-modal__lead">{{ $t('er_export_kpis_desc') }}</p>
            <div v-for="group in kpiGroups" :key="group.titleKey" class="export-modal__kpi-group">
              <p class="export-modal__sublabel">{{ $t(group.titleKey) }}</p>
              <div class="export-modal__checks" role="group" :aria-label="$t(group.titleKey)" :dir="textDir">
                <label
                  v-for="kpi in group.kpis"
                  :key="kpi.id"
                  class="export-modal__chip"
                  :class="{ 'is-on': !!selectedKpis[kpi.id] }"
                >
                  <input
                    type="checkbox"
                    :checked="!!selectedKpis[kpi.id]"
                    @change="toggleKpi(kpi.id)"
                  />
                  <span>{{ $t(kpi.labelKey) }}</span>
                </label>
              </div>
            </div>
          </section>
        </div>

        <footer class="export-modal__actions">
          <p v-if="exportSummary" class="export-modal__summary" :dir="textDir">{{ exportSummary }}</p>
          <div class="export-modal__action-row">
            <button type="button" class="export-modal__btn export-modal__btn--ghost" @click="$emit('cancel')">
              {{ $t('cancel') }}
            </button>
            <button
              type="button"
              class="export-modal__btn export-modal__btn--primary"
              :disabled="!canConfirm"
              @click="confirm"
            >
              {{ $t('download') }}
            </button>
          </div>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
import { lockPageScroll, unlockPageScroll } from '@/modules/common/utils/scrollLock'
import { getExecutiveReportMonths, getExecutiveReportWeeks } from '../services/executiveReportService'
import shiftService from '../../control-panel/services/shiftService'
import {
  EXECUTIVE_REPORT_KPIS,
  REPORT_GRAINS,
  WEEK_STARTS,
  defaultExecutiveKpiSelection,
} from '../consts/executiveReportKpis'

function monthKeyOrdinal(key) {
  const match = /^(\d{4})-(\d{2})$/.exec(String(key || ''))
  if (!match) return 0
  return Number(match[1]) * 12 + (Number(match[2]) - 1)
}

function shiftMonthKey(key, deltaMonths) {
  const match = /^(\d{4})-(\d{2})$/.exec(String(key || ''))
  if (!match) return ''
  const shifted = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1 + deltaMonths, 1))
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}`
}

function shiftDayKey(key, deltaDays) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(key || ''))
  if (!match) return ''
  const shifted = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + deltaDays))
  return shifted.toISOString().slice(0, 10)
}

function localIsoDay(date = new Date()) {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

export default {
  name: 'ExecutiveReportExportDialog',
  props: {
    visible: { type: Boolean, required: true },
    siteId: { type: [Number, String], default: null },
    panelTimeFrom: { type: String, default: '' },
    panelShiftId: { type: [Number, String], default: '' },
    isRTL: { type: Boolean, default: false },
    downloading: { type: Boolean, default: false },
  },
  data() {
    return {
      kpis: EXECUTIVE_REPORT_KPIS,
      reportGrains: REPORT_GRAINS,
      weekStarts: WEEK_STARTS,
      grain: REPORT_GRAINS.MONTH,
      weekStartsOn: WEEK_STARTS.SUNDAY,
      availablePeriods: [],
      periodCache: {},
      isLoadingPeriods: false,
      selectedReportPeriod: '',
      selectedFromPeriod: '',
      selectedComparePeriod: '',
      comparisonOn: true,
      availableShifts: [],
      selectedShiftIds: [],
      isLoadingShifts: false,
      selectedKpis: defaultExecutiveKpiSelection(),
      loadRequestId: 0,
      shiftLoadRequestId: 0,
    }
  },
  computed: {
    isWeekly() {
      return this.grain === REPORT_GRAINS.WEEK
    },
    textDir() {
      return this.isRTL ? 'rtl' : 'ltr'
    },
    periodCacheKey() {
      return `${this.siteId}:${this.grain}${this.isWeekly ? `:${this.weekStartsOn}` : ''}`
    },
    reportPeriodOptions() {
      if (!this.availablePeriods.length) return []
      const today = localIsoDay()
      const periods = this.isWeekly
        ? this.availablePeriods.filter((period) => period.timeTo < today)
        : this.availablePeriods
      if (!periods.length) return []
      if (!this.comparisonOn) return periods.slice().reverse()
      const earliest = periods[0]
      return periods.filter((period) => period.key > earliest.key).slice().reverse()
    },
    fromPeriodOptions() {
      return this.availablePeriods.slice().reverse()
    },
    toPeriodOptions() {
      const from = this.selectedFromPeriod
      return this.availablePeriods
        .filter((period) => !from || period.key >= from)
        .slice()
        .reverse()
    },
    isMonthRange() {
      return !this.isWeekly && !!this.selectedFromPeriod && !!this.selectedReportPeriod
        && this.selectedFromPeriod < this.selectedReportPeriod
    },
    periodHint() {
      if (this.isLoadingPeriods) return ''
      if (!this.availablePeriods.length) {
        return this.$t(this.isWeekly ? 'er_export_no_periods_week' : 'er_export_no_periods')
      }
      return ''
    },
    /**
     * The window the backend compares a month range against: the same number of months
     * ending immediately before the range starts.
     */
    rangeCompareWindow() {
      if (!this.isMonthRange) return { start: '', end: '' }
      const span = monthKeyOrdinal(this.selectedReportPeriod) - monthKeyOrdinal(this.selectedFromPeriod) + 1
      const end = shiftMonthKey(this.selectedFromPeriod, -1)
      return { start: shiftMonthKey(end, -(span - 1)), end }
    },
    /** A comparison needs the period before the selected one to have data of its own. */
    canCompare() {
      if (this.isLoadingPeriods || !this.availablePeriods.length) return false
      if (this.isMonthRange) {
        const { start, end } = this.rangeCompareWindow
        if (!start || !end) return false
        return this.availablePeriods.some((period) => period.key >= start && period.key <= end)
      }
      if (!this.selectedReportPeriod) return false
      return this.availablePeriods.some((period) => period.key < this.selectedReportPeriod)
    },
    showNoPreviousDataHint() {
      return !this.canCompare && !this.isLoadingPeriods && !!this.availablePeriods.length
    },
    comparePeriodOptions() {
      if (!this.selectedReportPeriod) return []
      return this.availablePeriods
        .filter((period) => period.key < this.selectedReportPeriod)
        .slice()
        .reverse()
    },
    selectedKpiIds() {
      return EXECUTIVE_REPORT_KPIS.filter((kpi) => this.selectedKpis[kpi.id]).map((kpi) => kpi.id)
    },
    allKpisSelected() {
      return EXECUTIVE_REPORT_KPIS.every((kpi) => this.selectedKpis[kpi.id])
    },
    kpiGroups() {
      const byId = {}
      this.kpis.forEach((kpi) => {
        byId[kpi.id] = kpi
      })
      return [
        {
          titleKey: 'er_page_performance_trends',
          ids: ['avgScore', 'responseTime', 'cleanerArrivals', 'supervisorInspections', 'expiredSlas'],
        },
        {
          titleKey: 'er_page_complaints_vs_visits',
          ids: ['complaints', 'visits', 'complaintsVsVisits', 'complaintDrivers'],
        },
      ]
        .map((group) => ({
          titleKey: group.titleKey,
          kpis: group.ids.map((id) => byId[id]).filter(Boolean),
        }))
        .filter((group) => group.kpis.length)
    },
    comparePresets() {
      const available = new Set(this.comparePeriodOptions.map((period) => period.key))
      const keyOrNull = (key) => (available.has(key) ? key : null)
      if (this.isWeekly) {
        return [{
          id: 'previous',
          labelKey: 'er_export_compare_previous_week',
          key: keyOrNull(shiftDayKey(this.selectedReportPeriod, -7)),
        }]
      }
      return [
        {
          id: 'previous',
          labelKey: 'er_export_compare_previous',
          key: keyOrNull(shiftMonthKey(this.selectedReportPeriod, -1)),
        },
        {
          id: 'lastYear',
          labelKey: 'er_export_compare_last_year',
          key: keyOrNull(shiftMonthKey(this.selectedReportPeriod, -12)),
        },
      ]
    },
    canConfirm() {
      return (
        !!this.selectedKpiIds.length &&
        !!this.selectedShiftIds.length &&
        !this.isLoadingShifts &&
        !!this.selectedReportPeriod &&
        (this.isWeekly || !!this.selectedFromPeriod) &&
        (!this.comparisonOn || this.isMonthRange || !!this.selectedComparePeriod) &&
        !this.isLoadingPeriods &&
        !this.downloading
      )
    },
    selectedReportLabel() {
      const period = this.reportPeriodOptions.find((item) => item.key === this.selectedReportPeriod)
      return period ? this.periodOptionLabel(period) : ''
    },
    selectedCompareLabel() {
      const period = this.comparePeriodOptions.find((item) => item.key === this.selectedComparePeriod)
      return period ? this.periodOptionLabel(period) : ''
    },
    reportShiftsParam() {
      return this.selectedShiftIds.join(',')
    },
    allShiftsSelected() {
      return !!this.availableShifts.length && this.selectedShiftIds.length === this.availableShifts.length
    },
    /** Names the shifts only when some are left out, so a full report stays unlabelled. */
    selectedShiftLabel() {
      if (this.allShiftsSelected) return ''
      return this.availableShifts
        .filter((shift) => this.selectedShiftIds.includes(shift.id))
        .map((shift) => shift.name)
        .join(', ')
    },
    rangeCompareLabel() {
      const { start, end } = this.rangeCompareWindow
      if (!start || !end) return ''
      return this.dateRangeLabel(this.monthKeyLabel(start), this.monthKeyLabel(end))
    },
    exportSummary() {
      if (this.isMonthRange) {
        const from = this.monthKeyLabel(this.selectedFromPeriod)
        const to = this.monthKeyLabel(this.selectedReportPeriod)
        const period = this.comparisonOn
          ? this.$t('er_export_summary_range_vs', {
              from: this.isolate(from),
              to: this.isolate(to),
              compare: this.isolate(this.rangeCompareLabel),
            })
          : this.$t('er_export_summary_range', { from: this.isolate(from), to: this.isolate(to) })
        return this.withShifts(period)
      }
      if (!this.selectedReportLabel) return ''
      const period = this.comparisonOn && this.selectedCompareLabel
        ? this.$t('er_export_summary', {
            report: this.isolate(this.selectedReportLabel),
            compare: this.isolate(this.selectedCompareLabel),
          })
        : this.comparisonOn
          ? ''
          : this.selectedReportLabel
      if (!period) return ''
      return this.withShifts(period)
    },
  },
  watch: {
    visible(isVisible) {
      if (isVisible) {
        lockPageScroll()
        this.selectedKpis = defaultExecutiveKpiSelection()
        this.loadPeriods()
        this.loadShifts()
        return
      }
      unlockPageScroll()
    },
    siteId() {
      this.periodCache = {}
      if (this.visible) {
        this.loadPeriods()
        this.loadShifts()
      }
    },
    grain() {
      if (this.visible) this.loadPeriods()
    },
    weekStartsOn() {
      if (this.visible && this.isWeekly) this.loadPeriods()
    },
    selectedReportPeriod() {
      if (this.selectedFromPeriod && this.selectedReportPeriod && this.selectedReportPeriod < this.selectedFromPeriod) {
        this.selectedFromPeriod = this.selectedReportPeriod
      }
      if (!this.comparisonOn || this.isMonthRange) return
      const valid = this.comparePeriodOptions.some((period) => period.key === this.selectedComparePeriod)
      if (!valid) this.selectedComparePeriod = this.defaultComparePeriod()
    },
    selectedFromPeriod(from) {
      if (from && this.selectedReportPeriod && this.selectedReportPeriod < from) {
        this.selectedReportPeriod = from
      }
    },
    comparisonOn() {
      if (this.visible) this.reconcileComparisonSelection()
    },
    canCompare(canCompare) {
      if (!canCompare) this.comparisonOn = false
    },
  },
  methods: {
    async loadPeriods() {
      const requestId = ++this.loadRequestId
      const cacheKey = this.periodCacheKey
      this.isLoadingPeriods = true
      try {
        let periods = this.periodCache[cacheKey]
        if (!periods) {
          periods = this.isWeekly
            ? await getExecutiveReportWeeks(this.siteId, this.weekStartsOn)
            : await getExecutiveReportMonths(this.siteId)
          this.$set(this.periodCache, cacheKey, periods)
        }
        if (requestId !== this.loadRequestId) return
        this.availablePeriods = periods
        this.applyDefaultPeriods()
      } catch (err) {
        if (requestId !== this.loadRequestId) return
        console.error(`[ExecutiveReportExportDialog] ${this.grain} periods failed`, err)
        this.availablePeriods = []
        this.applyDefaultPeriods()
      } finally {
        if (requestId === this.loadRequestId) this.isLoadingPeriods = false
      }
    },
    async loadShifts() {
      const requestId = ++this.shiftLoadRequestId
      this.isLoadingShifts = true
      try {
        const rows = this.siteId == null || this.siteId === '' ? [] : await shiftService.getShifts(this.siteId)
        if (requestId !== this.shiftLoadRequestId) return
        this.availableShifts = (Array.isArray(rows) ? rows : [])
          .filter((shift) => shift.is_active == null || Number(shift.is_active) === 1)
          .map((shift) => ({
            id: Number(shift.id),
            name: shift.shift_name || shift.shiftName || `Shift ${shift.id}`,
            startHour: Number(shift.start_hour),
          }))
          .sort((a, b) => {
            const aHour = Number.isFinite(a.startHour) ? a.startHour : 99
            const bHour = Number.isFinite(b.startHour) ? b.startHour : 99
            return aHour - bHour || a.name.localeCompare(b.name)
          })
        this.applyDefaultShift()
      } catch (err) {
        if (requestId !== this.shiftLoadRequestId) return
        console.error('[ExecutiveReportExportDialog] shifts failed', err)
        this.availableShifts = []
        this.selectedShiftIds = []
      } finally {
        if (requestId === this.shiftLoadRequestId) this.isLoadingShifts = false
      }
    },
    /** Starts from the one shift the panel is filtered to, otherwise from every active shift. */
    applyDefaultShift() {
      const panelKey = this.panelShiftId == null || this.panelShiftId === '' ? '' : String(this.panelShiftId)
      const panelShift = panelKey && this.availableShifts.find((shift) => String(shift.id) === panelKey)
      this.selectedShiftIds = panelShift ? [panelShift.id] : this.availableShifts.map((shift) => shift.id)
    },
    toggleShift(id) {
      this.selectedShiftIds = this.selectedShiftIds.includes(id)
        ? this.selectedShiftIds.filter((shiftId) => shiftId !== id)
        : [...this.selectedShiftIds, id]
    },
    toggleAllShifts() {
      this.selectedShiftIds = this.allShiftsSelected ? [] : this.availableShifts.map((shift) => shift.id)
    },
    applyDefaultPeriods() {
      const options = this.isWeekly ? this.reportPeriodOptions : this.fromPeriodOptions
      if (!options.length) {
        this.selectedReportPeriod = ''
        this.selectedFromPeriod = ''
        this.selectedComparePeriod = ''
        return
      }
      const panelKey = String(this.panelTimeFrom || '').slice(0, 7)
      const matchingPeriod = this.isWeekly ? null : options.find((period) => period.key === panelKey)
      const key = this.isWeekly
        ? this.defaultWeekKey(options)
        : (matchingPeriod ? matchingPeriod.key : options[0].key)
      this.selectedFromPeriod = this.isWeekly ? '' : key
      this.selectedReportPeriod = key
      this.selectedComparePeriod = this.defaultComparePeriod()
      if (!this.canCompare) this.comparisonOn = false
    },
    /**
     * Toggling the comparison keeps the period the user picked. Only a selection the
     * toggle itself invalidated is replaced, so the from/to months survive the switch.
     */
    reconcileComparisonSelection() {
      if (!this.availablePeriods.length) {
        this.applyDefaultPeriods()
        return
      }
      // The weekly list drops the earliest week while a comparison is on, since there
      // is no week before it to compare against.
      if (this.isWeekly && !this.reportPeriodOptions.some((period) => period.key === this.selectedReportPeriod)) {
        this.selectedReportPeriod = this.reportPeriodOptions.length ? this.reportPeriodOptions[0].key : ''
      }
      if (!this.comparisonOn || this.isMonthRange) return
      const valid = this.comparePeriodOptions.some((period) => period.key === this.selectedComparePeriod)
      if (!valid) this.selectedComparePeriod = this.defaultComparePeriod()
    },
    /**
     * The newest week that has already ended. The list is newest first, and a week
     * that still contains today is not the report a weekly review is asking for.
     */
    defaultWeekKey(options) {
      const today = localIsoDay()
      const completed = options.find((period) => period.timeTo < today)
      return (completed || options[0]).key
    },
    defaultComparePeriod() {
      if (!this.comparePeriodOptions.length) return ''
      const previousKey = this.isWeekly
        ? shiftDayKey(this.selectedReportPeriod, -7)
        : shiftMonthKey(this.selectedReportPeriod, -1)
      return this.comparePeriodOptions.some((period) => period.key === previousKey)
        ? previousKey
        : this.comparePeriodOptions[0].key
    },
    /**
     * Keeps one date, or one whole range, from being pulled across the dash or the
     * comparison word when the sentence runs right to left.
     */
    isolate(value) {
      return `\u2068${value}\u2069`
    },
    /** Start then end. The control's direction places the start on the leading side. */
    dateRangeLabel(start, end) {
      return `${this.isolate(start)} – ${this.isolate(end)}`
    },
    withShifts(period) {
      return this.selectedShiftLabel ? `${period} · ${this.isolate(this.selectedShiftLabel)}` : period
    },
    monthKeyLabel(key) {
      const match = /^(\d{4})-(\d{2})$/.exec(String(key || ''))
      if (!match) return key || ''
      const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1))
      return date.toLocaleDateString(this.$i18n.locale, {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      })
    },
    periodOptionLabel(period) {
      if (!this.isWeekly) {
        const date = new Date(Date.UTC(period.year, period.month - 1, 1))
        return date.toLocaleDateString(this.$i18n.locale, {
          month: 'long',
          year: 'numeric',
          timeZone: 'UTC',
        })
      }
      if (!period?.timeFrom || !period?.timeTo) return period?.label || period?.key || ''
      const start = new Date(`${period.timeFrom}T00:00:00Z`)
      const end = new Date(`${period.timeTo}T00:00:00Z`)
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return period.label || period.key
      const spansYears = start.getUTCFullYear() !== end.getUTCFullYear()
      const startLabel = start.toLocaleDateString(this.$i18n.locale, {
        month: 'short',
        day: 'numeric',
        year: spansYears ? 'numeric' : undefined,
        timeZone: 'UTC',
      })
      const endLabel = end.toLocaleDateString(this.$i18n.locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      })
      return this.dateRangeLabel(startLabel, endLabel)
    },
    toggleKpi(id) {
      this.$set(this.selectedKpis, id, !this.selectedKpis[id])
    },
    toggleAllKpis() {
      const selectAll = !this.allKpisSelected
      this.selectedKpis = EXECUTIVE_REPORT_KPIS.reduce((selection, kpi) => {
        selection[kpi.id] = selectAll
        return selection
      }, {})
    },
    confirm() {
      if (!this.canConfirm) return
      const period = this.isWeekly
        ? {
            grain: this.grain,
            reportWeek: this.selectedReportPeriod,
            weekStartsOn: this.weekStartsOn,
            comparison: this.comparisonOn,
            ...(this.comparisonOn ? { compareWeek: this.selectedComparePeriod } : {}),
            reportShifts: this.reportShiftsParam,
          }
        : {
            grain: this.grain,
            reportMonth: this.selectedReportPeriod,
            ...(this.isMonthRange ? { reportMonthFrom: this.selectedFromPeriod } : {}),
            comparison: this.comparisonOn,
            ...(this.comparisonOn && !this.isMonthRange ? { compareMonth: this.selectedComparePeriod } : {}),
            reportShifts: this.reportShiftsParam,
          }
      this.$emit('confirm', { selectedKpis: [...this.selectedKpiIds], period })
    },
  },
  beforeDestroy() {
    if (this.visible) unlockPageScroll()
  },
}
</script>
